import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../lib/api";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { toast } from "sonner";
import { ChevronLeft, Check, X, Mail, UserX, UserCheck } from "lucide-react";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: c } = useQuery({ queryKey: ["admin-customer", id], queryFn: async () => (await api.get(`/admin/customers/${id}`)).data });
  useEffect(() => { if (c) document.title = `${c.company_name} | Sambiva Admin`; }, [c]);

  const act = async (fn, msg) => { try { await fn(); qc.invalidateQueries(); toast.success(msg); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const setStatus = (status) => act(() => api.patch(`/admin/customers/${id}/status`, { status }), `Customer ${status.toLowerCase()}.`);
  const invite = () => act(() => api.post(`/admin/customers/${id}/invite`), "Account invitation sent.");
  const toggleAccount = (disable) => act(() => api.patch(`/admin/customers/${id}/account`, { status: disable ? "disable" : "enable" }), disable ? "Login disabled." : "Login reactivated.");

  if (!c) return <AdminLayout title="Customer"><div className="text-center py-16 text-sambiva-muted">Loading…</div></AdminLayout>;

  const Row = ({ label, value }) => (<div className="flex justify-between py-2.5 border-b border-black/5 last:border-0 text-sm"><span className="text-sambiva-muted">{label}</span><span className="text-sambiva-charcoal font-medium text-right max-w-[60%]">{value}</span></div>);

  return (
    <AdminLayout title={c.company_name}>
      <button onClick={() => navigate("/admin/customers")} className="flex items-center gap-1 text-sambiva-muted hover:text-sambiva-green mb-5 text-sm"><ChevronLeft size={16} /> Customers</button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-sambiva-charcoal">Company Details</h3><StatusBadge status={c.status} /></div>
            <Row label="Company" value={c.company_name} />
            <Row label="Contact Name" value={c.contact_name} />
            <Row label="Email" value={c.email} />
            <Row label="Phone" value={c.phone} />
            <Row label="VAT ID" value={c.vat_id} />
            <Row label="Country" value={c.country} />
            <Row label="Address" value={c.address} />
            <Row label="Postcode" value={c.postcode} />
            <Row label="Business Type" value={c.business_type} />
            <Row label="Application Date" value={new Date(c.created_at).toLocaleDateString()} />
            <Row label="Account" value={c.account ? (c.account.disabled ? "Disabled" : "Active") : "Not created"} />
          </div>

          {c.message && (
            <div className="bg-white rounded-2xl border border-black/5 p-6">
              <h3 className="font-semibold text-sambiva-charcoal mb-2">Application Message</h3>
              <p className="text-sm text-sambiva-muted leading-relaxed">{c.message}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <h3 className="font-semibold text-sambiva-charcoal mb-4">Order History</h3>
            {(c.orders || []).length === 0 ? <p className="text-sm text-sambiva-muted">No orders yet.</p> : (
              <div className="space-y-2">
                {c.orders.map((o) => (
                  <div key={o.id} onClick={() => navigate(`/admin/orders/${o.id}`)} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0 text-sm cursor-pointer hover:bg-sambiva-cream/40 px-2 -mx-2 rounded">
                    <span className="font-medium">{o.order_number}</span><span>€{o.total.toFixed(2)}</span><StatusBadge status={o.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-black/5 p-6 h-fit space-y-3">
          <h3 className="font-semibold text-sambiva-charcoal mb-2">Actions</h3>
          {c.status !== "Approved" && <button data-testid="approve-customer" onClick={() => setStatus("Approved")} className="w-full flex items-center justify-center gap-2 bg-sambiva-green text-white font-semibold py-2.5 rounded-xl text-sm hover:bg-sambiva-deepgreen transition-colors"><Check size={16} /> Approve</button>}
          {c.status !== "Rejected" && <button data-testid="reject-customer" onClick={() => setStatus("Rejected")} className="w-full flex items-center justify-center gap-2 bg-sambiva-red/10 text-sambiva-red font-semibold py-2.5 rounded-xl text-sm hover:bg-sambiva-red/20 transition-colors"><X size={16} /> Reject</button>}
          {c.status === "Approved" && <button data-testid="invite-customer" onClick={invite} className="w-full flex items-center justify-center gap-2 bg-sambiva-charcoal text-white font-semibold py-2.5 rounded-xl text-sm hover:bg-black transition-colors"><Mail size={16} /> {c.account ? "Resend Invitation" : "Send Account Invitation"}</button>}
          {c.account && !c.account.disabled && <button data-testid="disable-account" onClick={() => toggleAccount(true)} className="w-full flex items-center justify-center gap-2 border border-black/10 text-sambiva-charcoal font-semibold py-2.5 rounded-xl text-sm hover:bg-sambiva-cream"><UserX size={16} /> Disable Login</button>}
          {c.account && c.account.disabled && <button data-testid="enable-account" onClick={() => toggleAccount(false)} className="w-full flex items-center justify-center gap-2 border border-black/10 text-sambiva-charcoal font-semibold py-2.5 rounded-xl text-sm hover:bg-sambiva-cream"><UserCheck size={16} /> Reactivate Login</button>}
        </div>
      </div>
    </AdminLayout>
  );
}
